import { useMemo } from "react";
import { BetSlipMode } from "../types/enums";

interface UseCalculateWinningTaxProps {
  selections: IBetSlipSelection[];
  config: ITaxCalculationConfig;
  bonus?: IBetSlipBonus;
  stakes: { [key: string]: number };
  mode: BetSlipMode;
  bonusValue?: number; // Win Boost amount to include in taxable profit
  betBuilderOdds?: IBetBuilderTotalOdds[]; // Combined odds per bet builder event
}

export const useCalculateWinningTax = ({
  selections,
  config,
  bonus,
  stakes,
  mode,
  bonusValue = 0,
  betBuilderOdds = [],
}: UseCalculateWinningTaxProps) => {
  const taxCalculation = useMemo(() => {
    if (
      (!selections.length && !betBuilderOdds.length) ||
      !config.taxes.length
    ) {
      return {
        totalTaxValue: 0,
        taxPercentage: 0,
        isTaxRefund: false,
        taxBySelection: {},
      };
    }

    // Determine product type (1 = prematch, 2 = live, 3 = virtual)
    // Use count array to determine dominant type
    const getProductType = (sels: IBetSlipSelection[]) => {
      const count = [0, 0, 0]; // [prematch, live, virtual]
      sels.forEach((s) =>
        s.isVirtual ? count[2]++ : s.isLive ? count[1]++ : count[0]++,
      );
      // Return the product type with highest count (1-indexed)
      const dominantIndex = count.indexOf(Math.max(...count));
      return dominantIndex + 1; // 1=prematch, 2=live, 3=virtual
    };

    // Get the tax configuration for the product type
    const getTaxConfig = (type: number): ITaxInfo | undefined => {
      return config.taxes.find((t) => t.ProductTypeId === type);
    };

    // Find the applicable tax tier based on profit amount
    const findApplicableTier = (
      profit: number,
      taxConfig: ITaxInfo,
    ): ITaxTier | null => {
      if (!taxConfig.TaxTiers || taxConfig.TaxTiers.length === 0) {
        return null;
      }

      // Sort tiers by TaxProfitFloor descending to check from highest to lowest
      const sortedTiers = [...taxConfig.TaxTiers].sort(
        (a, b) => b.TaxProfitFloor - a.TaxProfitFloor,
      );

      // Find the first tier where profit is >= floor
      // If ceiling is 0, it means no upper limit
      for (const tier of sortedTiers) {
        if (profit >= tier.TaxProfitFloor) {
          if (tier.TaxProfitCeiling === 0 || profit <= tier.TaxProfitCeiling) {
            return tier;
          }
        }
      }

      // Profit exceeds all tier ceilings — fall back to the highest-floor tier
      // whose floor profit still clears (matches reference app behaviour).
      const fallbackTier = sortedTiers.find(
        (tier) => profit >= tier.TaxProfitFloor,
      );
      return fallbackTier ?? null;
    };

    // Calculate tax based on profit and applicable tier
    const calculateTaxValue = (
      payout: number,
      stake: number,
      taxConfig: ITaxInfo,
      bonusAmount: number = 0,
    ): TaxResult => {
      // Check if bonus should be included in taxable profit
      const includeBonus = taxConfig.IncludeBonus || false;

      // Calculate base profit
      let profit = payout - stake;

      // Add bonus to profit if IncludeBonus is true
      if (includeBonus && bonusAmount > 0) {
        profit += bonusAmount;
      }

      // Find the applicable tax tier
      const applicableTier = findApplicableTier(profit, taxConfig);

      // If no applicable tier or profit is below floor, no tax
      if (!applicableTier || profit <= applicableTier.TaxProfitFloor) {
        return { tax: 0, percentage: 0 };
      }

      // Calculate tax on the profit
      // Convert Rate from integer percentage (e.g., 20) to decimal (e.g., 0.20)
      const taxRate = applicableTier.Rate / 100;
      const taxAmount = profit * taxRate;

      return {
        tax: -Math.abs(taxAmount), // Tax is always negative (deduction)
        percentage: taxRate,
      };
    };

    const productType = getProductType(selections);
    const taxConfig = getTaxConfig(productType);

    // If no tax config found, return zero tax
    if (!taxConfig) {
      return {
        totalTaxValue: 0,
        taxPercentage: 0,
        isTaxRefund: false,
        taxBySelection: {},
      };
    }

    let totalTaxValue = 0;
    let displayTaxPercentage = 0;
    const taxBySelection: { [key: string]: number } = {};

    if (mode === BetSlipMode.Single) {
      selections.forEach((selection) => {
        const stake = stakes[selection.key] || 0;

        // Skip if no stake
        if (!stake) return;

        const payout = selection.oddPrice * stake;
        const taxResult = calculateTaxValue(
          payout,
          stake,
          taxConfig,
          bonusValue,
        );

        totalTaxValue += taxResult.tax;
        taxBySelection[selection.key] = taxResult.tax;
        displayTaxPercentage = taxResult.percentage;
      });
    } else if (mode === BetSlipMode.Combo) {
      const bbOddsProduct = betBuilderOdds.reduce((acc, bb) => acc * bb.odd, 1);
      const totalOdds = Number(
        selections
          .reduce((acc, sel) => acc * sel.oddPrice, bbOddsProduct)
          .toFixed(2),
      );
      const comboStake = stakes[BetSlipMode.Combo] || 0;

      // Skip if no stake
      if (!comboStake) {
        return {
          totalTaxValue: 0,
          taxPercentage: 0,
          isTaxRefund: false,
          taxBySelection: {},
        };
      }

      const comboPayout = totalOdds * comboStake;
      const taxResult = calculateTaxValue(
        comboPayout,
        comboStake,
        taxConfig,
        bonusValue,
      );
      totalTaxValue = taxResult.tax;
      displayTaxPercentage = taxResult.percentage;
    }

    return {
      totalTaxValue: Math.round(totalTaxValue * 100) / 100,
      taxPercentage: displayTaxPercentage,
      isTaxRefund: false,
      taxBySelection,
    };
  }, [selections, config, bonus, stakes, mode, bonusValue, betBuilderOdds]);

  return taxCalculation;
};
